import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  api,
  API,
  formatApiError,
} from "@/lib/api";

import PageHeader from "@/components/PageHeader";

import { Card } from "@/components/ui/card";

import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";

import { Label } from "@/components/ui/label";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { Badge } from "@/components/ui/badge";

import {
  Radio,
  Copy,
  Zap,
  Trash2,
  Plus,
  CheckCircle,
  Save,
} from "lucide-react";

import { toast } from "sonner";


export default function Integration() {
  const [settings, setSettings] = useState({
    wialon_base_url:
      "https://hst-api.wialon.com",

    wialon_token: "",

    fleetrun_base_url: "",

    fleetrun_token: "",

    company_name: "FleetCheck",
  });

  const [
    tokenMasked,
    setTokenMasked,
  ] = useState("");

  const [
    vehicles,
    setVehicles,
  ] = useState([]);

  const [
    templates,
    setTemplates,
  ] = useState([]);

  const [
    rules,
    setRules,
  ] = useState([]);

  const [
    users,
    setUsers,
  ] = useState([]);

  const [
    alertType,
    setAlertType,
  ] = useState("oil_change");

  const [
    vehicleId,
    setVehicleId,
  ] = useState("");

  const [
    odo,
    setOdo,
  ] = useState(50000);

  const [
    hours,
    setHours,
  ] = useState(3300);

  const [
    newAlert,
    setNewAlert,
  ] = useState("");

  const [
    newTemplate,
    setNewTemplate,
  ] = useState("");

  const [
    newAssignee,
    setNewAssignee,
  ] = useState("");


  const load = useCallback(
    async () => {
      try {
        const [
          vehiclesResponse,
          templatesResponse,
          rulesResponse,
          usersResponse,
          settingsResponse,
        ] = await Promise.all([
          api.get("/vehicles"),
          api.get("/templates"),
          api.get(
            "/mapping-rules"
          ),
          api.get("/users"),
          api.get("/settings"),
        ]);

        const vehicleData =
          vehiclesResponse.data || [];

        const templateData =
          templatesResponse.data || [];

        const ruleData =
          rulesResponse.data || [];

        const userData =
          usersResponse.data || [];

        const settingData =
          settingsResponse.data || {};

        setVehicles(vehicleData);

        setTemplates(
          templateData
        );

        setRules(ruleData);

        setUsers(userData);

        setSettings({
          wialon_base_url:
            settingData.wialon_base_url ||
            "https://hst-api.wialon.com",

          wialon_token: "",

          fleetrun_base_url:
            settingData.fleetrun_base_url ||
            "",

          fleetrun_token: "",

          company_name:
            settingData.company_name ||
            "FleetCheck",
        });

        setTokenMasked(
          settingData
            .wialon_token_masked ||
            ""
        );

        setVehicleId(
          (
            currentVehicleId
          ) => {
            if (
              currentVehicleId
            ) {
              return currentVehicleId;
            }

            return (
              vehicleData[0]?.id ||
              ""
            );
          }
        );
      } catch (e) {
        toast.error(
          formatApiError(
            e.response?.data?.detail
          )
        );
      }
    },
    []
  );


  useEffect(() => {
    load();
  }, [load]);


  const webhookUrl =
    `${API}/v1/fleetrun/trigger`;


  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(
        webhookUrl
      );

      toast.success(
        "URL copiada"
      );
    } catch {
      toast.error(
        "Não foi possível copiar a URL"
      );
    }
  };


  const saveSettings =
    async () => {
      try {
        await api.put(
          "/settings",
          settings
        );

        toast.success(
          "Configurações salvas"
        );

        await load();
      } catch (e) {
        toast.error(
          formatApiError(
            e.response?.data?.detail
          )
        );
      }
    };


  const simulate =
    async () => {
      if (!vehicleId) {
        toast.error(
          "Selecione um veículo"
        );

        return;
      }

      if (!alertType) {
        toast.error(
          "Selecione um tipo de alerta"
        );

        return;
      }

      try {
        const vehicle =
          vehicles.find(
            (item) =>
              item.id ===
              vehicleId
          );

        const { data } =
          await api.post(
            "/v1/fleetrun/trigger",
            {
              alert_type:
                alertType,

              vehicle_id:
                vehicleId,

              wialon_unit_id:
                vehicle?.wialon_unit_id,

              current_odometer:
                Number(odo),

              current_hours:
                Number(hours),

              message:
                `Alerta simulado ${alertType} · ${new Date().toLocaleString(
                  "pt-BR"
                )}`,
            }
          );

        if (data.ok) {
          toast.success(
            `Tarefa gerada! ID: ${
              data.task_id?.slice(
                0,
                8
              ) || ""
            }`
          );
        } else {
          toast.warning(
            data.message ||
              "Alerta processado sem criação de tarefa"
          );
        }
      } catch (e) {
        toast.error(
          formatApiError(
            e.response?.data?.detail
          )
        );
      }
    };


  const addRule =
    async () => {
      if (
        !newAlert ||
        !newTemplate
      ) {
        toast.error(
          "Preencha alerta e modelo"
        );

        return;
      }

      try {
        await api.post(
          "/mapping-rules",
          {
            alert_type:
              newAlert,

            checklist_template_id:
              newTemplate,

            default_assignee_id:
              newAssignee ||
              null,
          }
        );

        toast.success(
          "Regra criada"
        );

        setNewAlert("");
        setNewTemplate("");
        setNewAssignee("");

        await load();
      } catch (e) {
        toast.error(
          formatApiError(
            e.response?.data?.detail
          )
        );
      }
    };


  const delRule =
    async (id) => {
      try {
        await api.delete(
          `/mapping-rules/${id}`
        );

        toast.success(
          "Regra removida"
        );

        await load();
      } catch (e) {
        toast.error(
          formatApiError(
            e.response?.data?.detail
          )
        );
      }
    };


  const mechanicUsers =
    users.filter(
      (user) =>
        user.role ===
          "colaborador" &&
        user.sub_role ===
          "mecanico"
    );


  return (
    <div
      className="p-6 max-w-6xl mx-auto"
      data-testid="integration-page"
    >
      <PageHeader
        title="Fleetrun / Wialon"
        subtitle="Configurações de integração de telemetria e webhook"
      />


      <Card className="p-5 mb-4">
        <div className="flex items-center justify-between mb-4 gap-4">
          <div>
            <div className="font-display font-bold text-lg">
              Credenciais da API
            </div>

            <div className="text-xs text-muted-foreground">
              Configure tokens
              reais para
              sincronizar
              telemetria ao vivo
            </div>
          </div>

          <Button
            onClick={
              saveSettings
            }
            className="gap-2"
            data-testid="save-settings"
          >
            <Save className="w-4 h-4" />

            Salvar
          </Button>
        </div>


        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>
              Wialon Base URL
            </Label>

            <Input
              value={
                settings.wialon_base_url
              }
              onChange={(e) =>
                setSettings(
                  (
                    current
                  ) => ({
                    ...current,

                    wialon_base_url:
                      e.target
                        .value,
                  })
                )
              }
              className="font-mono text-sm"
              data-testid="wialon-base-url"
            />
          </div>


          <div>
            <Label>
              Wialon API Token
            </Label>

            <Input
              type="password"
              placeholder={
                tokenMasked ||
                "Cole seu token Wialon..."
              }
              value={
                settings.wialon_token
              }
              onChange={(e) =>
                setSettings(
                  (
                    current
                  ) => ({
                    ...current,

                    wialon_token:
                      e.target
                        .value,
                  })
                )
              }
              data-testid="wialon-token"
            />

            {tokenMasked && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <CheckCircle className="w-3 h-3 text-emerald-500" />

                Token salvo:{" "}
                {tokenMasked}
              </p>
            )}
          </div>


          <div>
            <Label>
              Fleetrun Base URL
            </Label>

            <Input
              value={
                settings.fleetrun_base_url
              }
              onChange={(e) =>
                setSettings(
                  (
                    current
                  ) => ({
                    ...current,

                    fleetrun_base_url:
                      e.target
                        .value,
                  })
                )
              }
              className="font-mono text-sm"
              placeholder="https://api.fleetrun.com"
              data-testid="fleetrun-base-url"
            />
          </div>


          <div>
            <Label>
              Fleetrun API Token
            </Label>

            <Input
              type="password"
              value={
                settings.fleetrun_token
              }
              onChange={(e) =>
                setSettings(
                  (
                    current
                  ) => ({
                    ...current,

                    fleetrun_token:
                      e.target
                        .value,
                  })
                )
              }
              data-testid="fleetrun-token"
            />
          </div>


          <div className="md:col-span-2">
            <Label>
              Nome da Empresa
            </Label>

            <Input
              value={
                settings.company_name
              }
              onChange={(e) =>
                setSettings(
                  (
                    current
                  ) => ({
                    ...current,

                    company_name:
                      e.target
                        .value,
                  })
                )
              }
              data-testid="company-name"
            />
          </div>
        </div>
      </Card>


      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center">
              <Radio className="w-5 h-5 text-primary" />
            </div>

            <div>
              <div className="font-display font-bold text-lg">
                Endpoint Webhook
              </div>

              <div className="text-xs text-muted-foreground">
                Configure este
                URL no Fleetrun
                para receber
                alertas de
                manutenção
              </div>
            </div>
          </div>


          <div className="flex gap-2">
            <Input
              value={
                webhookUrl
              }
              readOnly
              className="font-mono text-xs"
              data-testid="webhook-url"
            />

            <Button
              variant="outline"
              size="icon"
              onClick={
                copyUrl
              }
              data-testid="copy-webhook"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>


          <div className="mt-3 p-3 bg-secondary rounded-md text-xs">
            <div className="font-semibold mb-1">
              Payload esperado
              (POST JSON):
            </div>

            <pre className="font-mono text-[10px] leading-relaxed overflow-x-auto">
{`{
  "alert_type": "oil_change",
  "wialon_unit_id": "W-1001",
  "current_odometer": 50000,
  "current_hours": 3300,
  "message": "Troca de óleo necessária"
}`}
            </pre>
          </div>
        </Card>


        <Card className="p-5 border-primary/40 bg-primary/5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
              <Zap className="w-5 h-5 text-primary-foreground" />
            </div>

            <div>
              <div className="font-display font-bold text-lg">
                Simular Gatilho
                Fleetrun
              </div>

              <div className="text-xs text-muted-foreground">
                Dispara uma tarefa
                de manutenção mock
              </div>
            </div>
          </div>


          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>
                Tipo de Alerta
              </Label>

              <Select
                value={
                  alertType
                }
                onValueChange={
                  setAlertType
                }
              >
                <SelectTrigger data-testid="sim-alert-type">
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  {rules.map(
                    (rule) => (
                      <SelectItem
                        key={
                          rule.id
                        }
                        value={
                          rule.alert_type
                        }
                      >
                        {
                          rule.alert_type
                        }
                      </SelectItem>
                    )
                  )}

                  {rules.length ===
                    0 && (
                    <SelectItem value="oil_change">
                      oil_change
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>


            <div className="col-span-2">
              <Label>
                Veículo
              </Label>

              <Select
                value={
                  vehicleId
                }
                onValueChange={
                  setVehicleId
                }
              >
                <SelectTrigger data-testid="sim-vehicle">
                  <SelectValue placeholder="Selecione um veículo" />
                </SelectTrigger>

                <SelectContent>
                  {vehicles.map(
                    (
                      vehicle
                    ) => (
                      <SelectItem
                        key={
                          vehicle.id
                        }
                        value={
                          vehicle.id
                        }
                      >
                        #
                        {
                          vehicle.fleet_number
                        }{" "}
                        {
                          vehicle.plate
                        }{" "}
                        ·{" "}
                        {
                          vehicle.model
                        }
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>


            <div>
              <Label>
                Odômetro (km)
              </Label>

              <Input
                type="number"
                value={odo}
                onChange={(e) =>
                  setOdo(
                    e.target
                      .value
                  )
                }
                data-testid="sim-odo"
              />
            </div>


            <div>
              <Label>
                Horas
              </Label>

              <Input
                type="number"
                value={
                  hours
                }
                onChange={(e) =>
                  setHours(
                    e.target
                      .value
                  )
                }
                data-testid="sim-hours"
              />
            </div>
          </div>


          <Button
            onClick={
              simulate
            }
            className="w-full mt-4 h-11 gap-2 font-display font-bold uppercase"
            data-testid="simulate-trigger"
          >
            <Zap className="w-4 h-4" />

            Disparar Alerta
            Simulado
          </Button>
        </Card>
      </div>


      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display font-bold text-lg">
              Regras de
              Mapeamento
            </div>

            <div className="text-xs text-muted-foreground">
              Vincule alertas
              Fleetrun a modelos
              de checklist
            </div>
          </div>
        </div>


        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
          <Input
            placeholder="Tipo do alerta (ex: oil_change)"
            value={
              newAlert
            }
            onChange={(e) =>
              setNewAlert(
                e.target.value
              )
            }
            data-testid="rule-alert"
          />


          <Select
            value={
              newTemplate
            }
            onValueChange={
              setNewTemplate
            }
          >
            <SelectTrigger data-testid="rule-template">
              <SelectValue placeholder="Modelo" />
            </SelectTrigger>

            <SelectContent>
              {templates.map(
                (
                  template
                ) => (
                  <SelectItem
                    key={
                      template.id
                    }
                    value={
                      template.id
                    }
                  >
                    {
                      template.title
                    }
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>


          <Select
            value={
              newAssignee
            }
            onValueChange={
              setNewAssignee
            }
          >
            <SelectTrigger data-testid="rule-assignee">
              <SelectValue placeholder="Responsável" />
            </SelectTrigger>

            <SelectContent>
              {mechanicUsers.map(
                (user) => (
                  <SelectItem
                    key={
                      user.id
                    }
                    value={
                      user.id
                    }
                  >
                    {
                      user.name
                    }
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>


          <Button
            onClick={
              addRule
            }
            className="gap-2"
            data-testid="add-rule"
          >
            <Plus className="w-4 h-4" />

            Criar regra
          </Button>
        </div>


        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                Alerta
              </TableHead>

              <TableHead>
                Modelo
              </TableHead>

              <TableHead>
                Responsável
              </TableHead>

              <TableHead className="w-[70px]" />
            </TableRow>
          </TableHeader>


          <TableBody>
            {rules.map(
              (rule) => {
                const assignee =
                  users.find(
                    (user) =>
                      user.id ===
                      rule.default_assignee_id
                  );

                return (
                  <TableRow
                    key={
                      rule.id
                    }
                  >
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="font-mono"
                      >
                        {
                          rule.alert_type
                        }
                      </Badge>
                    </TableCell>

                    <TableCell>
                      {rule
                        .template
                        ?.title ||
                        "—"}
                    </TableCell>

                    <TableCell>
                      {assignee
                        ?.name ||
                        "Não definido"}
                    </TableCell>

                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          delRule(
                            rule.id
                          )
                        }
                        data-testid={`del-rule-${rule.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              }
            )}

            {rules.length ===
              0 && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center text-muted-foreground py-8"
                >
                  Nenhuma regra
                  de mapeamento
                  cadastrada.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}